import { DeleteSweep, Send } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useAiChatSendMessage } from '~/api/ai-chat/send-message';
import { useAppContext } from '~/contexts';
import { AiChatRole, type AiChatMessageInput } from '~/generated/graphql';
import { Typography } from '../Typography';
import {
  ChatActionButton,
  ChatActions,
  ChatActionsRow,
  ChatRoot,
  ContentEditor,
  ContentEditorWrapper,
  MessageBubble,
  MessageInput,
  MessageList,
  MessageRow,
  TypingIndicator,
} from './styles';

const MAX_CHARS = 500;
const MAX_ROWS = 6;
const LINE_HEIGHT = 20; // ~15px font * 1.35 line-height

export function AIChat() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<AiChatMessageInput[]>([]);
  const [input, setInput] = useState('');
  const [sendAiMessage, { loading }] = useAiChatSendMessage();

  const listRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const scrollToBottom = () => {
    const messageList = listRef.current;
    if (!messageList) return;

    requestAnimationFrame(() => {
      messageList.scrollTop = messageList.scrollHeight;
    });
  };

  const clearChat = () => {
    setMessages([]);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const clamp = (s: string) => s.slice(0, MAX_CHARS);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const maxHeight = LINE_HEIGHT * MAX_ROWS;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflow = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const { currentNetwork } = useAppContext();

  const sendMessage = async (text: string) => {
    if (loading) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // 1. Add user message locally
    const newMessages: AiChatMessageInput[] = [
      ...messages,
      { role: AiChatRole.User, content: trimmed },
    ];
    setMessages(newMessages);
    scrollToBottom();

    try {
      // 2. Call GraphQL mutation with full history
      const { data } = await sendAiMessage({
        variables: {
          input: {
            networkId: currentNetwork.id,
            messages: newMessages,
          },
        },
      });

      const assistantReply = data?.aiChatSendMessage?.message;

      // 3. Append assistant response
      setMessages((prev) => [
        ...prev,
        {
          role: AiChatRole.Assistant,
          content: assistantReply ?? t('graphql.errors.mutations.ai_chat.no_response_failure'),
        },
      ]);
      scrollToBottom();
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        {
          role: AiChatRole.Assistant,
          content: t('graphql.errors.mutations.ai_chat.other_error_failure'),
        },
      ]);
    }
  };

  return (
    <ChatRoot>
      {/* role=log announces each new message politely without re-reading the transcript. */}
      <MessageList aria-label={t('components.ai_chat.conversation')} ref={listRef} role="log">
        {messages.map((m, idx) => {
          const isUser = m.role === AiChatRole.User;
          return (
            <MessageRow key={idx} isUser={isUser}>
              <MessageBubble isUser={isUser}>
                <ReactMarkdown>{m.content}</ReactMarkdown>
              </MessageBubble>
            </MessageRow>
          );
        })}
        {loading && (
          <TypingIndicator>
            {/* The text beside it says what is happening. */}
            <CircularProgress aria-hidden size={16} />
            <Typography color="text.secondary" sx={{ fontSize: 14 }}>
              {t('components.ai_chat.ai_thinking')}
            </Typography>
          </TypingIndicator>
        )}
      </MessageList>
      <MessageInput>
        <ContentEditorWrapper>
          <ContentEditor
            ref={textareaRef}
            value={input}
            placeholder={t('components.ai_chat.query_placeholder')}
            readOnly={loading}
            rows={1}
            onChange={(e) => {
              setInput(clamp(e.target.value));
              resizeTextarea();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void sendMessage(input);
              }
            }}
          />
        </ContentEditorWrapper>

        <ChatActions>
          <ChatActionsRow>
            <ChatActionButton
              onClick={() => void sendMessage(input)}
              disabled={loading || !input.trim()}
              title={t('components.ai_chat.send')}
              Icon={Send}
            />
            <ChatActionButton
              onClick={clearChat}
              disabled={loading}
              title={t('components.ai_chat.clear_chat')}
              Icon={DeleteSweep}
            />
          </ChatActionsRow>
        </ChatActions>
      </MessageInput>
    </ChatRoot>
  );
}
